<template>
	<Dialog :visible="true" style="width: 65vw" modal :closable="false">
		<template #header>
			<div class="w-full">
				<div class="flex justify-between">
					<h1>
						Copy Step<FontAwesomeIcon
							:icon="['fa', 'spinner']"
							class="text-blue-500 mt-3 ml-2"
							spin
							v-show="loadingSelectionDefault || props.spinnerRuns"
						/>
					</h1>

					<div class="flex gap-2">
						<Button
							id="btnBackToPRList"
							type="button"
							@click="emit('cancel')"
							class="btn bg-redSignal"
							:disabled="props.spinnerRuns"
							v-show="!props.spinnerRuns"
							>Cancel</Button
						>
						<!-- <button id="btnSaveProject" class="btn btn-primary" @click="save()" :disabled="props.spinnerRuns">Create Copy</button> -->
						<Button class="ml-4 btn btn-primary" @click="save" :disabled="props.spinnerRuns">
							<template #default>
								<span v-if="props.spinnerRuns">
									<FontAwesomeIcon :icon="['fa', 'spinner']" spin class="mr-2" />
									Copying...
								</span>
								<span v-else> Create Copy </span>
							</template>
						</Button>
					</div>
				</div>

				<div class="flex items-start mt-6">
					<WarningIcon
						errorType="Warning"
						:tooltipText="[
							'Only raw material and output mass are copied. All other inputs return to default.',
							'Intermediates must be added to the copy manually to link steps',
						]"
						size="lg"
						class="mt-3"
					/>

					<div class="ml-2 text-red-500">
						<span v-if="!copyUpstreamSteps && !copyFromTemplate"
							>Only raw material and output mass are copied. All other inputs return to default.</span
						>
						<span v-if="!copyUpstreamSteps && !copyFromTemplate" class="block mt-1"
							>Intermediates must be added to the copy manually to link steps</span
						>
						<span v-if="copyUpstreamSteps || copyFromTemplate" class="text-blue-600"
							>All upstream steps, raw materials, and intermediate links will be copied as a group.</span
						>
					</div>
				</div>
				<div class="flex items-center gap-2 mt-4" v-if="props.tabName === tabsDefinitions.syntheticAPI.value && props.isTemplateCopyEnabled">
					<span class="text-sm text-gray-600">Copy from Templates</span>
					<ToggleSwitch v-model="copyFromTemplate" @change="onTemplateToggle" />
				</div>
				<div class="flex justify-between mt-4" v-if="props.tabName === tabsDefinitions.syntheticAPI.value && selectedStepIdToCopyFrom !== ''">
					<I2FormField label="New Step Name" class="mr-6">
						<InputText
							v-model="newStepName"
							placeholder="select a step"
							:class="{ 'p-invalid': (saveAttempted && !newStepName) || isNameDuplicate }"
							ref="newStepNameRef"
						/>
					</I2FormField>
				</div>
				<div v-if="copyFromTemplate && upstreamStepNames.length > 0" class="mt-4">
					<div class="text-sm font-medium mb-2">Upstream Step Names</div>
					<div v-for="us in upstreamStepNames" :key="us.id" class="flex items-center gap-2 mb-2">
						<span class="text-xs text-gray-500 w-16">{{ us.originalNumber }}</span>
						<InputText
							v-model="us.newName"
							class="flex-1"
							:class="{ 'p-invalid': (saveAttempted && !us.newName) || props.actualSteps.some((step) => step.stepName === us.newName) }"
						/>
					</div>
				</div>
				<div
					class="flex items-center gap-2 mt-4"
					v-if="selectedStepIdToCopyFrom !== '' && props.tabName === tabsDefinitions.syntheticAPI.value && !copyFromTemplate"
				>
					<span class="text-sm text-gray-600">Copy upstream steps</span>
					<ToggleSwitch v-model="copyUpstreamSteps" />
					<span v-if="copyUpstreamSteps" class="text-xs text-blue-500 ml-2">All upstream steps and intermediate links will be copied</span>
				</div>
				<div class="flex justify-between mt-4">
					<I2FormField label="Project" class="mr-6">
						<Select
							v-model="selectedProjectId"
							:options="projectList"
							optionLabel="projectName"
							option-value="id"
							placeholder="Select Project"
							class="w-full md:w-14rem"
							:filter="projectList && projectList.length > 10"
							@change="onProjectChange"
							:class="{ 'p-invalid': saveAttempted && !selectedProjectId, null: selectedProjectId !== null }"
							:disabled="loadingSelectionDefault"
						>
						</Select>
					</I2FormField>
					<I2FormField label="Subproject" class="mr-6">
						<Select
							ref="subProjectDropdown"
							v-model="selectedSubProjectId"
							:options="subProjectList"
							optionLabel="subProjectName"
							option-value="id"
							placeholder="Select Subproject"
							class="w-full md:w-14rem"
							:filter="subProjectList && subProjectList.length > 10"
							@change="onSubProjectChange"
							:class="{ 'p-invalid': saveAttempted && !selectedSubProjectId, null: selectedSubProjectId !== null }"
							:disabled="loadingSelectionDefault"
						>
							<template #value="slotProps">
								<div v-if="slotProps.value" class="flex align-items-center">
									<div>{{ getSelectedSubProjectName(slotProps.value) }}</div>
								</div>
								<span v-else>
									{{ slotProps.placeholder }}
								</span>
							</template>
							<template #option="slotProps">
								<div class="flex align-items-center">
									<div>
										{{ slotProps.option.subProjectName }} ({{
											slotProps.option.assessment.filter(
												(item: any) =>
													item.isActive === true && item.step.length > 0 && item.step.find((itemStep: Step) => itemStep.screenId === props.screenId)
											).length
										}})
									</div>
								</div>
							</template>
						</Select>
					</I2FormField>
					<I2FormField label="Assessment" class="mr-6">
						<Select
							ref="assessmentDropdown"
							v-model="selectedAssessmentId"
							:options="assessmentList"
							optionLabel="assessmentName"
							option-value="id"
							placeholder="Select Assessment"
							class="w-full md:w-14rem"
							:filter="assessmentList && assessmentList.length > 10"
							@change="onAssessmentChange"
							:class="{ 'p-invalid': saveAttempted && !selectedAssessmentId, null: selectedAssessmentId !== null }"
							:disabled="loadingSelectionDefault"
						>
						</Select>
					</I2FormField>
				</div>

				<DataTable
					:value="steps"
					:selection="selectedStep"
					@update:selection="selectedStep = $event"
					responsiveLayout="scroll"
					:row-hover="true"
					dataKey="id"
					scrollable
					scrollHeight="250px"
					class="mt-4"
					selectionMode="single"
					@row-select="stepSelection"
					:class="{ 'p-invalid': saveAttempted && !selectedStep, null: selectedStep !== null }"
				>
					<template #empty> No steps found. </template>
					<template #loading> Loading steps data. Please wait. </template>
					<Column field="stepName" :row-reorder="false">
						<template #header><I2Label label="Step Name" helpElementName="CopyStepDialogStepName" /></template>
						><template #body="{ data }"> {{ data.stepNumber }} - {{ data.stepName }}</template>
					</Column>
				</DataTable>
			</div>
		</template>
	</Dialog>
	<Toast position="top-right" />
</template>
<script setup lang="ts">
import I2Label from "@/crispify/crispy-components/I2Label.vue"
import { ref, computed, onMounted, reactive, nextTick } from "vue"
import { ApiClient, SubProject, Step, Project, Assessment } from "../../entities/NSwagGenerated"
import { SessionInfo, PageAccess } from "../auth/personSessionData"

import Dialog from "primevue/dialog"
import InputText from "primevue/inputtext"
import ToggleSwitch from "primevue/toggleswitch"

import * as Session from "../auth/personSessionData"
import { useToast } from "primevue/usetoast"

import DataTable, { DataTableRowSelectEvent } from "primevue/datatable"
import Column from "primevue/column"
import Dropdown, { DropdownChangeEvent } from "primevue/dropdown"
import { library } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { faTriangleExclamation, faSpinner } from "@fortawesome/free-solid-svg-icons"
library.add(faTriangleExclamation, faSpinner)
import WarningIcon from "./warningIcon.vue"
import { useTabsDefinitions } from "../composables/tabsDefinitions"
import Button from "primevue/button"
const tabsDefinitions = useTabsDefinitions()
const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const saveAttempted = ref(false)
const copyFromTemplate = ref(false)
const copyUpstreamSteps = ref(false)
const allProjects = ref([] as Project[])

let toast = useToast()
const props = defineProps({
	showDialog: { type: Boolean, required: true },
	screenId: { type: String, required: true },
	assessmentId: { type: String, required: true },
	tabName: { type: String, required: true },
	actualSteps: {
		type: Array,
		required: true,
		validator(steps: Step[]) {
			return steps.every((step: Step) => step instanceof Step)
		},
	},
	spinnerRuns: { type: Boolean, require: true },
	isTemplateCopyEnabled: { type: Boolean, required: false, default: false },
})
const emit = defineEmits<{
	(e: "cancel"): void
	(e: "save", selectedStepIdToCopyFrom: string, newStepName: string, copyUpstream: boolean, upstreamNames?: Record<string, string>): void
}>()

const selectedProjectId = ref()
const selectedSubProjectId = ref()
const selectedAssessmentId = ref()
const projectList = ref([] as Project[])
const subProjectList = ref([] as SubProject[])
const assessmentList = ref([] as Assessment[])
const subProjectDropdown = ref(null)
const assessmentDropdown = ref(null)
const newStepNameRef = ref(null)
const steps = ref()
const newStepName = ref("")
const selectedStep = ref()
const loadingSelectionDefault = ref(false)
const upstreamStepNames = ref<{ id: string; originalNumber: number; originalName: string; newName: string }[]>([])
onMounted(() => {
	fillProjectList().then(() => setDefaultValues())
})

const selectedStepIdToCopyFrom = ref("")
const isNameDuplicate = computed(() => props.actualSteps.some((step) => step.stepName === newStepName.value))

const stepSelection = async (event: DataTableRowSelectEvent) => {
	newStepName.value = event.data.stepName + (props.tabName === tabsDefinitions.syntheticAPI.value ? "-copy" : "")
	selectedStepIdToCopyFrom.value = event.data.id
	// Load upstream steps for template copy mode
	if (copyFromTemplate.value) {
		try {
			const upstream = await apiClient.getUpstreamStepsList(event.data.id)
			upstreamStepNames.value = upstream.map((s: Step) => ({
				id: s.id ?? "",
				originalNumber: s.stepNumber ?? 0,
				originalName: s.stepName ?? "",
				newName: (s.stepName ?? "") + "-copy",
			}))
		} catch {
			upstreamStepNames.value = []
		}
	} else {
		upstreamStepNames.value = []
	}
	// focus for the user
	await nextTick()
	if (newStepNameRef.value) {
		newStepNameRef.value.$el.focus()
		const inputElement = newStepNameRef.value.$el as HTMLInputElement
		const length = inputElement.value.length
		inputElement.setSelectionRange(length - 5, length)
	}
}

const setDefaultValues = () => {
	loadingSelectionDefault.value = true
	apiClient.getSingleAssessmentById(props.assessmentId).then(
		(assessment) => {
			apiClient.getActiveSubProjects(assessment.subProject!.project!.id, props.screenId, props.assessmentId).then(
				(res) => {
					selectedProjectId.value = assessment.subProject!.projectId
					subProjectList.value = res
					if (res && res.length > 0) {
						selectedSubProjectId.value = assessment.subProject!.id
						apiClient.getActiveAssessments(assessment.subProject!.id, props.screenId, props.assessmentId).then(
							(res) => {
								if (res && res.length > 0) {
									assessmentList.value = res
									selectedAssessmentId.value = props.assessmentId
									apiClient.getActiveSteps(props.assessmentId, props.screenId).then(
										(res) => {
											steps.value = res
											loadingSelectionDefault.value = false
										},
										(err) => {
											throw new Error(`Network response was not ok: ${err}`)
										}
									)
								} else {
									loadingSelectionDefault.value = false
									// if (props.tabName === tabsDefinitions.productProcess.value) {
									// 	toast.add({
									// 		severity: "warn",
									// 		summary: "Warning",
									// 		detail: "There is no Dose Form chosen in the selected sub project.",
									// 		life: 2500,
									// 	})
									// }
								}
							},
							(err) => {
								throw new Error(`Network response was not ok: ${err}`)
							}
						)
					} else {
						loadingSelectionDefault.value = false
						if (props.tabName === tabsDefinitions.productProcess.value) {
							toast.add({
								severity: "warn",
								summary: "Warning",
								detail: "There is no Step in the selected project.",
								life: 2500,
							})
						}
					}
				},
				(err) => {
					throw new Error(`Network response was not ok: ${err}`)
				}
			)
		},
		(err) => {
			throw new Error(`Network response was not ok: ${err}`)
		}
	)
}

const fillProjectList = async () => {
	try {
		const res = await apiClient.getActiveProjects(props.screenId, props.assessmentId)
		allProjects.value = res
		projectList.value = res
	} catch (err) {
		throw new Error(`Network response was not ok: ${err}`)
	}
}

const onProjectChange = (event: DropdownChangeEvent) => {
	apiClient.getActiveSubProjects(event.value, props.screenId, props.assessmentId).then(
		(res) => {
			subProjectList.value = res
			steps.value = null
			selectedSubProjectId.value = null
			selectedAssessmentId.value = null
			subProjectDropdown.value!.$el.click()
		},
		(err) => {
			throw new Error(`Network response was not ok: ${err}`)
		}
	)
}
const onSubProjectChange = (event: DropdownChangeEvent) => {
	apiClient.getActiveAssessments(event.value, props.screenId, props.assessmentId).then(
		(res) => {
			selectedAssessmentId.value = null
			steps.value = null
			assessmentList.value = res
			assessmentDropdown.value!.$el.click()
		},
		(err) => {
			throw new Error(`Network response was not ok: ${err}`)
		}
	)
}
const onAssessmentChange = (event: DropdownChangeEvent) => {
	apiClient.getActiveSteps(event.value, props.screenId).then(
		(res) => {
			steps.value = res
		},
		(err) => {
			throw new Error(`Network response was not ok: ${err}`)
		}
	)
}

const getSelectedSubProjectName = (selectedValue: string) => {
	// Find the selected subproject based on ID
	const selectedSubProject = subProjectList.value.find((item: SubProject) => item.id === selectedValue)
	return selectedSubProject ? selectedSubProject.subProjectName : ""
}

function testFields() {
	var errorMessage = ""
	const stepExists = props.actualSteps.some((step) => step.stepName === newStepName.value)
	if (selectedStepIdToCopyFrom.value === "" || newStepName.value === "" || (stepExists && props.tabName === tabsDefinitions.syntheticAPI.value)) {
		if (stepExists) {
			errorMessage = "The name chosen is already in use in this assessment"
		} else if (selectedStepIdToCopyFrom.value === "" || newStepName.value === "") {
			errorMessage =
				props.tabName === tabsDefinitions.syntheticAPI.value && newStepName.value === "" && selectedStepIdToCopyFrom.value !== ""
					? "You must assign a name to copy a step"
					: "You must select a step"
		}
		toast.add({
			severity: "error",
			summary: "Error",
			detail: errorMessage,
			life: 2500,
		})
		saveAttempted.value = true
		return false
	}
	return true
}
function save() {
	if (!testFields()) return
	// Build upstream step names map for template copy
	const upstreamNames: Record<string, string> = {}
	if (copyFromTemplate.value && upstreamStepNames.value.length > 0) {
		for (const us of upstreamStepNames.value) {
			upstreamNames[us.id] = us.newName
		}
	}
	emit(
		"save",
		selectedStepIdToCopyFrom.value,
		newStepName.value,
		copyUpstreamSteps.value,
		Object.keys(upstreamNames).length > 0 ? upstreamNames : undefined
	)
}

const onTemplateToggle = async () => {
	if (copyFromTemplate.value) {
		// Load template projects and filter the project list
		try {
			const result = await apiClient.getTemplateProjects()
			if (result && result.length > 0) {
				projectList.value = result
				selectedProjectId.value = null
				selectedSubProjectId.value = null
				selectedAssessmentId.value = null
				subProjectList.value = []
				assessmentList.value = []
				steps.value = null
				copyUpstreamSteps.value = true
				upstreamStepNames.value = []
			} else {
				toast.add({
					severity: "warn",
					summary: "Warning",
					detail: "No template projects have been configured. An admin must designate at least one project as a template project.",
					life: 5000,
				})
				copyFromTemplate.value = false
			}
		} catch (err) {
			toast.add({
				severity: "error",
				summary: "Error",
				detail: "Failed to load template projects",
				life: 3000,
			})
			copyFromTemplate.value = false
		}
	} else {
		// Restore full project list and reset to defaults
		projectList.value = allProjects.value
		copyUpstreamSteps.value = false
		setDefaultValues()
	}
}
</script>
<style scoped>
.p-highlight {
	background-color: #007bff !important;
	color: white !important;
}
.p-invalid {
	border-color: red !important;
}
.button-container {
	margin: 10px;
}
</style>
